import React, { useState, useEffect } from 'react';
import InternalPageLayout from '../components/InternalPageLayout';
import { Camera, Video, Calendar, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import axios from 'axios';
import { getImageUrl, getBackendURL } from '../utils/assetUrlHelper';

const API_URL = getBackendURL();

export default function MediaPage() {
  const [photoCategories, setPhotoCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, videosRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/api/media/photo-categories`),
        axios.get(`${API_URL}/api/media/videos`),
        axios.get(`${API_URL}/api/news?limit=6`)
      ]);

      setPhotoCategories(categoriesRes.data);
      setVideos(videosRes.data.slice(0, 6));
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Error fetching media data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <InternalPageLayout
      title="Media Center"
      subtitle="Photos, videos, and news from ICAR-CISH"
      breadcrumbs={[{ label: 'Media' }]}
    >
      {/* Photos Section */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Camera className="w-6 h-6 text-[#0E6D2B]" />
            Photo Gallery
          </h2>
          <Link to="/photos">
            <button className="text-[#0E6D2B] hover:text-[#0a5a23] font-medium flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="aspect-square rounded-lg" />)}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {photoCategories.slice(0, 4).map(category => (
              <Link key={category.id} to={`/photos?category=${category.id}`}>
                <div className="group relative aspect-square rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer">
                  <img
                    src={getImageUrl(category.cover_image)}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <h3 className="font-bold text-lg">{category.name}</h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Videos Section */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Video className="w-6 h-6 text-[#0E6D2B]" />
            Videos
          </h2>
          <Link to="/videos">
            <button className="text-[#0E6D2B] hover:text-[#0a5a23] font-medium flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="space-y-2">
                <Skeleton className="aspect-video rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.slice(0, 3).map(video => (
              <Link key={video.id} to={`/videos#${video.id}`}>
                <div className="group">
                  <div className="relative aspect-video rounded-lg overflow-hidden mb-3 shadow-md group-hover:shadow-xl transition-shadow">
                    <img
                      src={getImageUrl(video.thumbnail_image)}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                        <Video className="w-8 h-8 text-[#0E6D2B]" />
                      </div>
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-800 group-hover:text-[#0E6D2B] transition-colors">
                    {video.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* News & Events Section */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-[#0E6D2B]" />
            News & Events
          </h2>
          <Link to="/events">
            <button className="text-[#0E6D2B] hover:text-[#0a5a23] font-medium flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-32 rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.slice(0, 3).map(event => (
              <Link key={event.id} to={`/events/${event.id}`}>
                <div className="bg-white border rounded-xl p-5 hover:shadow-lg transition-shadow">
                  {event.banner_image_url && (
                    <img
                      src={getImageUrl(event.banner_image_url)}
                      alt={event.title}
                      className="w-full h-32 object-cover rounded-lg mb-3"
                    />
                  )}
                  <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2">{event.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-3">{event.body?.replace(/<[^>]*>/g, '')}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </InternalPageLayout>
  );
}
